import type { IAccordionCard } from "@dinesapp/dines-ui/components/Accordion/Types/Accordion";
import type { IconDefinition } from "@/icons/solid-icons";

/**
 * Enum representing the different statuses for the redeemables navbar.
 * Each status corresponds to a specific view in the redeemables section.
 */
export enum ListRedeemablesNavbarStatus {
  Overview = "overview",
  Enhanced = "enhanced",
  Standard = "standard",
  CodeLookup = "code-lookup",
  Packages = "packages",
}

/**
 * Interface representing the structure of a navbar status item in the redeemables section.
 *
 * @property {string} id - A unique identifier for the navbar status.
 * @property {ListRedeemablesNavbarStatus} status - The status type of the navbar item.
 * @property {string} title - The display title for the navbar item.
 * @property {string} link - The URL link associated with the navbar item.
 * @property {boolean} selected - Indicates whether this navbar item is currently selected.
 * @property {() => void} onClick - A function to be called when the navbar item is clicked.
 */
export interface IListRedeemablesNavbarStatuses {
  id: string;
  status: ListRedeemablesNavbarStatus;
  title: string;
  link: string;
  selected: boolean;
  onClick: () => void;
}

/**
 * Interface representing the layout view model for the redeemables section.
 *
 * @property {Object} labels - Contains label information for the view.
 * @property {string} labels.title - The title to be displayed in the layout.
 * @property {Array<IListRedeemablesNavbarStatuses>} statuses - An array of navbar status items.
 */
export interface IListRedeemablesLayoutViewModel {
  labels: {
    title: string;
  };
  statuses: Array<IListRedeemablesNavbarStatuses>;
}

export enum RedeemablesTypes {
  Discount = "discount_code",
  Voucher = "voucher_code",
  PrepaidTab = "prepaid_tab",
}

export enum RedeemablesStatus {
  All = "all",
  Enabled = "enabled",
  Disabled = "disabled",
}

export enum RedeemableClassification {
  Discount = 0,
  PrepaidTab = 1,
  Voucher = 2,
}

/**
 * IChecklistItem defines the structure of an item in a checklist.
 *
 * @property {string} id - A unique identifier for the checklist item.
 * @property {string} label - The display label for the checklist item.
 */
export interface IChecklistItem {
  id: string;
  label: string;
}

/**
 * IChecklist defines the structure of a checklist, which contains a title,
 * an icon, and a list of checklist items.
 *
 * @property {string} title - The title of the checklist.
 * @property {IconDefinition} icon - The icon associated with the checklist.
 * @property {Array<IChecklistItem>} children - An array of checklist items.
 */
export interface IChecklist {
  id: string;
  title: string;
  icon: IconDefinition;
  children: Array<IChecklistItem>;
}

/**
 * IInfoViewModel defines the structure for the information view model
 * used in the Redeemable Configurations and related screens.
 *
 * @property {Object} labels - Contains textual information for the info section.
 * @property {string} labels.id - Unique identifier for the info section.
 * @property {string} labels.description - Main description text.
 * @property {string} labels.subtitle - Subtitle or key benefit label.
 * @property {string} labels.subDescription - Additional descriptive text.
 *
 * @property {Omit<IAccordionCard, "children">} infoWrapper - Configuration for the accordion card UI component,
 * excluding its children property.
 *
 * @property {Array<IChecklist>} checklist - An array of checklists, each containing a title, icon, and items.
 */
export interface IInfoViewModel {
  labels: {
    id: string;
    description: string;
    subtitle: string;
    subDescription: string;
  };
  infoWrapper: Omit<IAccordionCard, "children">;
  checklist: Array<IChecklist>;
}
